import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import { Col } from 'reactstrap';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import EditIcon from '@mui/icons-material/Edit';
import VApplicationsListDataService from '@services/VApplicationsListDataService';
import AppContext from '@contexts/AppContext';
import TableSortLabel from '@mui/material/TableSortLabel';
import ReactLoading from 'react-loading';
import Moment from 'moment';
import GlobalFilteringFields from '@components/Filtering/GlobalFilteringFields';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  head: {
    backgroundColor: '#efefef',
    color: theme.palette.common.black,
  },
  body: {
    fontSize: '14px', // Set font size for the body cells only
  },
}));

const useStyles2 = {
  minWidth: 500,
};

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const DEFAULT_APP_STATUS = [];
const DEFAULT_ADV_STATUS = ['Active Advance Receivable'];

export default function CustomPaginationActionsTable() {
  const appContext = useContext(AppContext);

  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [filters, setFilters] = useState(appContext.accountingActiveAdvanceReceivableFilters);
  const [orderBy, setOrderBy] = useState('');
  const [orderDir, setOrderDir] = useState('ASC');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pages, setPages] = useState(undefined);

  const navigate = useNavigate();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const loadData = (filters) => {
    setFilters(filters);

    let reqFilters = filters;
    if (reqFilters && reqFilters.client) {
      reqFilters = { ...reqFilters, ClientPublicId: reqFilters.client.publicId };
    }
    if (reqFilters && reqFilters.attorney) {
      reqFilters = { ...reqFilters, AttorneyPublicId: reqFilters.attorney.publicId };
    }
    if (reqFilters && reqFilters.lawFirm) {
      reqFilters = { ...reqFilters, LawFirmPublicId: reqFilters.lawFirm.publicId };
    }
    if (reqFilters.applicationNo) reqFilters.ApplicationNo = reqFilters.applicationNo;
    if (reqFilters.advanceNo) reqFilters.AdvanceNo = reqFilters.advanceNo;
    if (reqFilters.caseStatus) reqFilters.ApplicationCaseStatus = reqFilters.caseStatus;
    if (reqFilters.portfolioName) reqFilters.PortfolioPublicId = reqFilters.portfolioName;
    if (reqFilters.accidentTypeName) reqFilters.AccidentTypeName = reqFilters.accidentTypeName;
    if (reqFilters.applicationStatus) reqFilters.ApplicationStatus = reqFilters.applicationStatus;
    if (reqFilters.advanceStatus) reqFilters.AdvanceStatus = reqFilters.advanceStatus;
    if (reqFilters.paymentConfinrmationNo)
      reqFilters.PaymentConfinrmationNo = reqFilters.paymentConfinrmationNo;

    delete reqFilters.applicationNo;
    delete reqFilters.advanceNo;
    delete reqFilters.client;
    delete reqFilters.caseStatus;
    delete reqFilters.lawFirm;
    delete reqFilters.attorney;
    delete reqFilters.portfolioName;
    delete reqFilters.accidentTypeName;
    delete reqFilters.applicationStatus;
    delete reqFilters.advanceStatus;
    delete reqFilters.paymentConfinrmationNo;

    setIsLoading(true);

    VApplicationsListDataService.search(reqFilters, orderBy, orderDir, page, null, rowsPerPage)
      .then((response) => {
        setRows(response.data.data);
        setPages(response.data.pages.slice(-1)[0]);
        setIsLoading(false);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!filters.dateRange || filters.dateRange === '') {
      let tempFilters = {
        ...filters,
        dateRange: 'This month',
      };

      if (!filters.advanceStatus) {
        tempFilters = {
          ...tempFilters,
          advanceStatus: DEFAULT_ADV_STATUS,
        };
      }

      setFilters(tempFilters);
      appContext.accountingActiveAdvanceReceivableFilters = tempFilters;
      localStorage.setItem('accountingActiveAdvanceReceivableFilters', JSON.stringify(tempFilters));
      loadData(tempFilters);
    } else {
      loadData(filters);
    }
  }, [page, orderBy, orderDir, rowsPerPage]);

  const update = (filter) => {
    let reqFilters = filter;
    if (reqFilters && reqFilters.client) {
      reqFilters = { ...reqFilters, ClientPublicId: reqFilters.client.publicId };
    }
    if (reqFilters && reqFilters.attorney) {
      reqFilters = { ...reqFilters, AttorneyPublicId: reqFilters.attorney.publicId };
    }
    if (reqFilters && reqFilters.lawFirm) {
      reqFilters = { ...reqFilters, LawFirmPublicId: reqFilters.lawFirm.publicId };
    }
    if (reqFilters.applicationNo) reqFilters.ApplicationNo = reqFilters.applicationNo;
    if (reqFilters.advanceNo) reqFilters.AdvanceNo = reqFilters.advanceNo;
    if (reqFilters.caseStatus) reqFilters.ApplicationCaseStatus = reqFilters.caseStatus;
    if (reqFilters.portfolioName) reqFilters.PortfolioPublicId = reqFilters.portfolioName;
    if (reqFilters.accidentTypeName) reqFilters.AccidentTypeName = reqFilters.accidentTypeName;
    if (reqFilters.applicationStatus) reqFilters.ApplicationStatus = reqFilters.applicationStatus;
    if (reqFilters.advanceStatus) reqFilters.AdvanceStatus = reqFilters.advanceStatus;
    if (reqFilters.paymentConfinrmationNo)
      reqFilters.PaymentConfinrmationNo = reqFilters.paymentConfinrmationNo;

    delete reqFilters.applicationNo;
    delete reqFilters.advanceNo;
    delete reqFilters.client;
    delete reqFilters.caseStatus;
    delete reqFilters.lawFirm;
    delete reqFilters.attorney;
    delete reqFilters.portfolioName;
    delete reqFilters.accidentTypeName;
    delete reqFilters.applicationStatus;
    delete reqFilters.advanceStatus;
    delete reqFilters.paymentConfinrmationNo;

    setIsLoading(true);

    VApplicationsListDataService.search(reqFilters, orderBy, orderDir, page, null, rowsPerPage)
      .then((response) => {
        setRows(response.data.data);
        setPages(response.data.pages.slice(-1)[0]);
        setIsLoading(false);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const searchWithFilters = (filter) => {
    const newFilters = { ...filter };
    setFilters(newFilters);
    appContext.accountingActiveAdvanceReceivableFilters = newFilters;
    localStorage.setItem('accountingActiveAdvanceReceivableFilters', JSON.stringify(newFilters));
    if (page === 1) update(newFilters);
    else setPage(1);
  };

  const getAll = () => {
    const tempFilters = {
      dateRange: 'This month',
      AdvanceStatus: DEFAULT_ADV_STATUS,
    };

    setFilters(tempFilters);
    appContext.accountingActiveAdvanceReceivableFilters = tempFilters;
    localStorage.setItem('accountingActiveAdvanceReceivableFilters', JSON.stringify(tempFilters));

    searchWithFilters(tempFilters);
  };

  const [activeFieldForSorting, setActiveFieldForSorting] = useState();

  const arrange = (field) => {
    if (orderBy === field) {
      if (orderDir === 'ASC') {
        setOrderDir('DESC');
      } else {
        setOrderDir('ASC');
      }
    } else {
      setOrderBy(field);
      setOrderDir('ASC');
    }

    setPage(1);
  };

  const handlePageSizeChange = (event) => {
    setRowsPerPage(event.target.value);
  };

  return (
    <>
      <GlobalFilteringFields
        appContextFilter={filters}
        searchWithFilters={searchWithFilters}
        getAll={getAll}
        defaultApplicationStatus={DEFAULT_APP_STATUS}
        defaultAdvanceStatus={DEFAULT_ADV_STATUS}
      />

      <Col md="10" className="float-right">
        <TableContainer component={Paper}>
          <Table className={useStyles2} aria-label="custom pagination table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Id</StyledTableCell>

                <StyledTableCell style={{ width: '15%' }}>CLIENT</StyledTableCell>

                <StyledTableCell style={{ width: '8%' }}>APP STATUS</StyledTableCell>
                <StyledTableCell style={{ width: '8%' }}>ADV STATUS</StyledTableCell>

                <StyledTableCell
                  style={{ width: '8%' }}
                  onClick={() => {
                    arrange('applicationNo');
                    setActiveFieldForSorting('applicationNo');
                  }}
                >
                  APP #
                  <TableSortLabel
                    active={activeFieldForSorting === 'applicationNo'}
                    direction={
                      activeFieldForSorting === 'applicationNo' ? orderDir.toLowerCase() : 'asc'
                    }
                    onClick={() => arrange('applicationNo')}
                  />
                </StyledTableCell>

                <StyledTableCell
                  style={{ width: '8%' }}
                  onClick={() => {
                    arrange('advaceNo');
                    setActiveFieldForSorting('advaceNo');
                  }}
                >
                  ADV #
                  <TableSortLabel
                    active={activeFieldForSorting === 'advaceNo'}
                    direction={
                      activeFieldForSorting === 'advaceNo' ? orderDir.toLowerCase() : 'asc'
                    }
                    onClick={() => arrange('advaceNo')}
                  />
                </StyledTableCell>

                <StyledTableCell style={{ width: '10%' }}>TYPE OF ACCIDENT</StyledTableCell>

                <StyledTableCell
                  style={{ width: '10%' }}
                  onClick={() => {
                    arrange('applicationDate');
                    setActiveFieldForSorting('applicationDate');
                  }}
                >
                  ADV. DATE
                  <TableSortLabel
                    active={activeFieldForSorting === 'applicationDate'}
                    direction={
                      activeFieldForSorting === 'applicationDate' ? orderDir.toLowerCase() : 'asc'
                    }
                    onClick={() => arrange('applicationDate')}
                  />
                </StyledTableCell>

                <StyledTableCell style={{ width: '12%' }}>ADVANCED AMOUNT</StyledTableCell>

                <StyledTableCell style={{ width: '10%' }}>ATTORNEY</StyledTableCell>

                {/* <StyledTableCell
                  style={{ width: '15%' }}
                >
                  LAST NOTE
                </StyledTableCell> */}

                <StyledTableCell>ACTIONS</StyledTableCell>
              </TableRow>
            </TableHead>
            {isLoading === false && (
              <TableBody>
                {rows &&
                  rows.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell component="th" scope="row">
                        {row.id}
                      </TableCell>
                      <TableCell style={{ width: '15%' }}>{row.client}</TableCell>
                      <TableCell style={{ width: '8%' }}>{row.applicationStatus}</TableCell>
                      <TableCell style={{ width: '8%' }}>{row.advanceStatus}</TableCell>
                      <TableCell style={{ width: '8%' }}>{row.applicationNo}</TableCell>
                      <TableCell style={{ width: '8%' }}>{row.advanceNo}</TableCell>
                      <TableCell style={{ width: '10%' }}>{row?.accidentTypeName}</TableCell>
                      <TableCell style={{ width: '10%' }}>
                        {row?.applicationDate &&
                          Moment(row.applicationDate.split('T')[0]).format('MM-DD-YYYY')}
                      </TableCell>
                      <TableCell style={{ width: '12%' }}>
                        {row?.advancedAmount != null &&
                          moneyFormatter.format(parseFloat(row?.advancedAmount).toFixed(2))}
                      </TableCell>
                      <TableCell style={{ width: '10%' }}>{row.attorney}</TableCell>
                      {/* <TableCell style={{ width: '15%' }}>{row.lastNote}</TableCell> */}

                      <TableCell className="p-2">
                        <button
                          type="button"
                          className="btn float-right"
                          onClick={() => {
                            navigate(
                              '/underwriting/applications/' + row.applicationNo + '/attorney',
                            );
                          }}
                        >
                          <EditIcon />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            )}
            {isLoading && (
              <TableBody>
                <TableRow>
                  <td aria-label="Table Body" />
                  <td>
                    <ReactLoading type="bars" color="#7E8299" />
                  </td>
                  <td aria-label="Table Row" />
                </TableRow>
              </TableBody>
            )}

            <TableFooter />
          </Table>
        </TableContainer>
        <div className="float-left mt-5">
          <select onChange={handlePageSizeChange} className="form-control" defaultValue={15}>
            {[5, 10, 15, 25, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="float-right mt-5">
          <Pagination
            count={pages}
            page={page}
            siblingCount={1}
            boundaryCount={1}
            variant="outlined"
            onChange={handleChangePage}
          />
        </div>
      </Col>
    </>
  );
}
